import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, Tag } from 'antd';
import { searchDictionaryQuery } from '../queries';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

type SynonymPreviewBoxProps = {
  /** 저장 전 초안 — 있으면 세트에 이 줄을 반영했다고 보고 분석한다(세트는 바뀌지 않는다) */
  draft?: { terms: string[]; ruleId?: string };
  /** 처음 넣어 둘 검색어 — 보통 이 줄의 첫 토큰 */
  initialText?: string;
  /** 검색어를 고정하고 입력칸을 감춘다(빠른 편집 모달) */
  fixedText?: string;
};

/**
 * 「고객이 이렇게 검색하면 어떤 말로 나뉘고 무엇과 함께 찾아지는가」를 저장 전에 보여 준다.
 * 운영자가 결과를 보고 판단할 수 있어야 개발자에게 되묻는 왕복이 사라진다(설계 D-①).
 */
export const SynonymPreviewBox = (props: SynonymPreviewBoxProps): React.JSX.Element => {
  const { draft, initialText = '', fixedText } = props;
  const [text, setText] = useState(initialText);
  const searchText = (fixedText ?? text).trim();
  const debouncedText = useDebouncedValue(searchText);
  const debouncedDraft = useDebouncedValue(draft);

  const preview = useQuery({
    ...searchDictionaryQuery.preview({
      text: debouncedText,
      ...(debouncedDraft && debouncedDraft.terms.length ? { draft: debouncedDraft } : {}),
    }),
    enabled: debouncedText.length > 0,
    keepPreviousData: true,
    staleTime: 30_000,
  });

  const data = preview.data;
  return (
    <div className="preview-box">
      {fixedText === undefined && (
        <Input
          aria-label="미리보기 검색어"
          placeholder="고객이 검색창에 넣을 말"
          value={text}
          onChange={(event) => setText(event.target.value)}
          allowClear
        />
      )}
      {fixedText !== undefined && (
        <div className="preview-row">
          <span className="preview-label">검색어</span>
          <Tag>{fixedText}</Tag>
        </div>
      )}
      {preview.isError && <div className="preview-row">미리보기를 불러오지 못했습니다.</div>}
      {data && searchText && (
        <>
          <div className="preview-row">
            <span className="preview-label">나뉜 말</span>
            {data.baseTokens.map((token) => (
              <Tag key={token}>{token}</Tag>
            ))}
          </div>
          <div className="preview-row">
            <span className="preview-label">함께 찾는 말</span>
            {data.expandedTokens.length ? (
              data.expandedTokens.map((token) => <Tag key={token}>{token}</Tag>)
            ) : (
              <span className="settings-muted">없음</span>
            )}
            {data.matchedRuleIds.length > 1 && (
              <span className="spread-note">· {data.matchedRuleIds.length}개 줄로 퍼짐</span>
            )}
          </div>
          {data.draftApplied && <div className="preview-row settings-muted">저장 전 결과입니다.</div>}
        </>
      )}
    </div>
  );
};
